import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ResetPasswordPage from "./ResetPasswordPage";
import ResetPasswordConfirm from "./ResetPasswordConfirm"; // you’ll create this file
import { analyze } from "./lib/api";
import { appInsights, reactPlugin } from "./telemetry";

// Core UI components
import DatPublicAbout from "./components/dat_public_about";
import Logo from "./components/Logo";
import About from "./components/About";
import PrivacyDisclaimer from "./components/PrivacyDisclaimer";
import WhatDATisNot from "./components/WhatDATisNot";
import DATandBayes from "./components/DATandBayes";
import Step2Tension from "./components/Step2Tension";
import IllustrativeExample from "./components/IllustrativeExample";
import { MathJax, MathJaxContext } from "better-react-mathjax";

import {
  renderDomainBubble,
  injectCitationsIntoText,
  cleanSummary,
  getRelevantCitations,
} from "./utils/referenceUtils.jsx";
import { generateRelatedQuestion } from "./utils/referenceUtils.jsx";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import katex from "katex";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { getAnalyticsInstance } from "./firebase"; // <-- CORRECT WAY for analytics!
import { logEvent } from "firebase/analytics";
import { auth } from "./firebase";

function pickReformulated(entry) {
  const v1 = entry?.output?.reformulated_question;
  const v2 = entry?.output?.step4?.summary?.reformulated;
  const v3 = entry?.output?.step4?.reformulated;
  const s = [v1, v2, v3].find((v) => typeof v === "string" && v.trim());
  return s ? s.trim() : "";
}

function containsMathJax(text) {
  if (typeof text !== "string") return false;
  // inline $...$ or block $$...$$ or \(...\) or \[...\]
  return /(\${1,2}[\s\S]+?\${1,2}|\\\(|\\\[)/.test(text);
}

function splitIllustrativePdfRuns(text = "") {
  const source = cleanIllustrativeExample(text);

  const mathPattern =
    /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$[^$\n]+?\$)/g;

  const runs = [];
  let lastIndex = 0;
  let match;

  while ((match = mathPattern.exec(source)) !== null) {
    if (match.index > lastIndex) {
      runs.push({
        type: "text",
        value: source.slice(lastIndex, match.index),
      });
    }

    const raw = match[0];
    const explicitlyDisplay = raw.startsWith("$$") || raw.startsWith("\\[");

    let latex;

    if (raw.startsWith("$$")) {
      latex = raw.slice(2, -2);
    } else if (raw.startsWith("\\[")) {
      latex = raw.slice(2, -2);
    } else if (raw.startsWith("\\(")) {
      latex = raw.slice(2, -2);
    } else {
      latex = raw.slice(1, -1);
    }

    const displayMode = explicitlyDisplay;

    runs.push({
      type: "math",
      value: latex.trim(),
      displayMode,
    });

    lastIndex = mathPattern.lastIndex;
  }

  if (lastIndex < source.length) {
    runs.push({
      type: "text",
      value: source.slice(lastIndex),
    });
  }

  return runs;
}

async function renderLatexForPdf(latex, displayMode = false) {
  const host = document.createElement("span");
  Object.assign(host.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    display: "inline-block",
    width: "max-content",
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#000000",
    padding: displayMode ? "16px 12px" : "8px 6px",
    overflow: "visible",
    fontSize: "18px",
    lineHeight: "normal",
    whiteSpace: "nowrap",
    zIndex: "-1",
  });

  document.body.appendChild(host);
  try {
    // Use true KaTeX display mode so fractions get correct vertical layout.
    katex.render(latex, host, {
      displayMode,
      throwOnError: false,
      strict: "ignore",
      output: "html",
    });

    if (document.fonts?.ready) await document.fonts.ready;

    // html2canvas previously produced a visible bitmap in this application.
    // html-to-image/toPng returned a valid but blank image in Firefox.
    const scale = 3;
    const canvas = await html2canvas(host, {
      backgroundColor: "#ffffff",
      scale,
      logging: false,
      useCORS: true,
    });

    if (canvas.width < 2 || canvas.height < 2) {
      throw new Error("DAT PDF: equation capture has zero dimensions");
    }

    // Fail visibly rather than silently placing a white image in the PDF.
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("DAT PDF: equation canvas unavailable");
    const rgba = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let visible = false;
    for (let i = 0; i < rgba.length; i += 16) {
      if (
        rgba[i + 3] > 100 &&
        (rgba[i] < 190 || rgba[i + 1] < 190 || rgba[i + 2] < 190)
      ) {
        visible = true;
        break;
      }
    }
    if (!visible) {
      throw new Error("DAT PDF: equation captured as blank image");
    }

    return {
      dataUrl: canvas.toDataURL("image/png"),
      aspectRatio: canvas.width / canvas.height,
      cssWidth: canvas.width / scale,
      cssHeight: canvas.height / scale,
    };
  } finally {
    host.remove();
  }
}

function drawMathOnPdf(doc, rendered, x, y, width, height) {
  doc.addImage(rendered.dataUrl, "PNG", x, y, width, height, undefined, "FAST");
}

async function prepareIllustrativePdfParts(text) {
  const cleaned = cleanIllustrativeExample(text);

  if (!containsMathJax(cleaned)) {
    return {
      hasMath: false,
      parts: [{ type: "text", value: cleaned }],
    };
  }

  const rawRuns = splitIllustrativePdfRuns(cleaned);
  const parts = [];

  for (const run of rawRuns) {
    if (run.type === "math") {
      const rendered = await renderLatexForPdf(run.value, run.displayMode);

      parts.push({
        ...run,
        rendered,
      });
    } else {
      parts.push(run);
    }
  }

  return {
    hasMath: parts.some((part) => part.type === "math"),
    parts,
  };
}

async function addPdfIllustrativeExample(doc, title, prepared, options) {
  const pageHeight = doc.internal.pageSize.height;
  const bottomMargin = 14;
  const x = 14;
  const maxWidth = 180;
  const rightEdge = x + maxWidth;
  const lineHeight = 6;
  const fontSize = 10;

  let y = options.y;

  const newSectionPage = () => {
    doc.addPage();
    y = 14;
  };

  // Do not orphan the Illustrative Example heading.
  if (y + 36 > pageHeight - bottomMargin) {
    newSectionPage();
  }

  y = addPdfText(doc, title, {
    y,
    pageHeight,
    fontSize: 12,
    fontStyle: "bold",
  });

  y += 2;

  let cursorX = x;
  let cursorY = y;
  let lineHasContent = false;
  let pendingSpace = false;

  const setTextStyle = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
  };

  const startNewPage = () => {
    doc.addPage();
    cursorX = x;
    cursorY = 14;
    lineHasContent = false;
    pendingSpace = false;
    setTextStyle();
  };

  const ensureVerticalSpace = (needed = lineHeight) => {
    if (cursorY + needed > pageHeight - bottomMargin) {
      startNewPage();
    }
  };

  const nextLine = () => {
    cursorY += lineHeight;
    cursorX = x;
    lineHasContent = false;
    pendingSpace = false;
    ensureVerticalSpace();
  };

  const writeNativeText = (value) => {
    const tokens = String(value).split(/(\n|\s+)/);

    for (const token of tokens) {
      if (!token) continue;

      if (token === "\n") {
        if (lineHasContent) {
          nextLine();
        } else {
          pendingSpace = false;
        }
        continue;
      }

      if (/^\s+$/.test(token)) {
        pendingSpace = true;
        continue;
      }

      setTextStyle();

      let spaceWidth = 0;

      if (pendingSpace && lineHasContent) {
        spaceWidth = doc.getTextWidth(" ");
      }

      const tokenWidth = doc.getTextWidth(token);

      if (lineHasContent && cursorX + spaceWidth + tokenWidth > rightEdge) {
        nextLine();
        spaceWidth = 0;
      }

      ensureVerticalSpace();

      if (spaceWidth > 0) {
        cursorX += spaceWidth;
      }

      doc.text(token, cursorX, cursorY);

      cursorX += tokenWidth;
      lineHasContent = true;
      pendingSpace = false;
    }
  };

  const writeDisplayMath = async (part) => {
    if (lineHasContent) {
      nextLine();
    }

    pendingSpace = false;

    const maxDisplayWidth = 160;
    // Match the nominal 10 pt PDF text scale, while preserving math proportions.
    const mmPerCssPixel = 0.22;
    let renderWidth = part.rendered.cssWidth * mmPerCssPixel;
    let renderHeight = part.rendered.cssHeight * mmPerCssPixel;

    if (renderWidth > maxDisplayWidth) {
      const scale = maxDisplayWidth / renderWidth;
      renderWidth *= scale;
      renderHeight *= scale;
    }

    if (cursorY + renderHeight + 4 > pageHeight - bottomMargin) {
      startNewPage();
    }

    const renderX = x + (maxWidth - renderWidth) / 2;

    drawMathOnPdf(
      doc,
      part.rendered,
      renderX,
      cursorY,
      renderWidth,
      renderHeight,
    );

    cursorY += renderHeight + 4;
    cursorX = x;
    lineHasContent = false;
    pendingSpace = false;
  };

  const writeInlineMath = async (part) => {
    setTextStyle();

    const inlineHeight = 4.8;
    let renderWidth = inlineHeight * part.rendered.aspectRatio;

    // Very long "inline" expressions are better treated
    // as display maths than compressed into a prose line.
    if (renderWidth > maxWidth * 0.75) {
      await writeDisplayMath({
        ...part,
        displayMode: true,
      });
      return;
    }

    let spaceWidth = 0;

    if (pendingSpace && lineHasContent) {
      spaceWidth = doc.getTextWidth(" ");
    }

    if (lineHasContent && cursorX + spaceWidth + renderWidth > rightEdge) {
      nextLine();
      spaceWidth = 0;
    }

    ensureVerticalSpace();

    if (spaceWidth > 0) {
      cursorX += spaceWidth;
    }

    drawMathOnPdf(
      doc,
      part.rendered,
      cursorX,
      cursorY - inlineHeight * 0.8,
      renderWidth,
      inlineHeight,
    );

    cursorX += renderWidth;
    lineHasContent = true;
    pendingSpace = false;
  };

  for (let i = 0; i < prepared.parts.length; i += 1) {
    const part = prepared.parts[i];
    if (part.type === "text") {
      let value = part.value;
      // An equation-final full stop belongs to the displayed formula,
      // not as an orphan at the start of the following prose line.
      if (
        i > 0 &&
        prepared.parts[i - 1].type === "math" &&
        prepared.parts[i - 1].displayMode
      ) {
        value = value.replace(/^\s*\.\s+(?=[A-Z])/, " ");
      }
      writeNativeText(value);
      continue;
    }

    if (part.displayMode) {
      await writeDisplayMath(part);
    } else {
      await writeInlineMath(part);
    }
  }

  if (lineHasContent) {
    cursorY += lineHeight;
  }

  return cursorY + 4;
}

function pdfValuePreserveLines(value) {
  if (value === null || value === undefined) return "—";

  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => pdfValuePreserveLines(item))
      .filter((item) => item && item !== "—");

    return cleaned.length ? cleaned.join("\n") : "—";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  const text = String(value)
    .replace(/\u00a0/g, " ")
    .replace(/[→➙⇒]/g, "->")
    .replace(/[←⇐]/g, "<-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/…/g, "...")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\u00C0-\u00FF\u2013\u2014]/g, "")
    .split(/\r?\n/)
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text || "—";
}

function addPdfText(doc, text, options) {
  const {
    x = 14,
    y,
    maxWidth = 180,
    lineHeight = 6,
    pageHeight,
    bottomMargin = 14,
    fontSize = 10,
    fontStyle = "normal",
  } = options;

  let cursorY = y;

  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(fontSize);

  const lines = pdfValuePreserveLines(text)
    .split("\n")
    .flatMap((line) => doc.splitTextToSize(line, maxWidth));

  lines.forEach((line) => {
    if (cursorY + lineHeight > pageHeight - bottomMargin) {
      doc.addPage();
      cursorY = 14;
    }

    doc.text(line, x, cursorY);
    cursorY += lineHeight;
  });

  return cursorY;
}

function addPdfSection(doc, title, body, options) {
  const pageHeight = doc.internal.pageSize.height;
  let y = options.y;

  // Avoid leaving a section heading alone at the bottom of a page.
  if (y + 14 > pageHeight - 14) {
    doc.addPage();
    y = 14;
  }

  y = addPdfText(doc, title, {
    y,
    pageHeight,
    fontSize: 12,
    fontStyle: "bold",
  });

  y += 2;

  if (Array.isArray(body)) {
    body.forEach((item) => {
      if (item === null || item === undefined || item === "") return;

      y = addPdfText(doc, item, {
        y,
        pageHeight,
        fontSize: 10,
      });

      y += 2;
    });
  } else {
    y = addPdfText(doc, body, {
      y,
      pageHeight,
      fontSize: 10,
    });
  }

  return y + 4;
}

function addPdfSubsection(doc, title, body, options) {
  const pageHeight = doc.internal.pageSize.height;
  let y = options.y;

  if (
    body === null ||
    body === undefined ||
    (typeof body === "string" && (!body.trim() || body.trim() === "N/A"))
  ) {
    return y;
  }

  if (y + 12 > pageHeight - 14) {
    doc.addPage();
    y = 14;
  }

  y = addPdfText(doc, title, {
    y,
    pageHeight,
    fontSize: 10,
    fontStyle: "bold",
  });

  y += 1;

  y = addPdfText(doc, body, {
    y,
    pageHeight,
    fontSize: 10,
    fontStyle: "normal",
  });

  return y + 4;
}

function addPdfFooters(doc, footerText) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    doc.text(pdfValuePreserveLines(footerText), 14, pageHeight - 8);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - 38, pageHeight - 8);
  }
}

export async function downloadHistoryAsPDF(history) {
  const preparedIllustrativeExamples = await Promise.all(
    history.map(async (entry) => {
      const example =
        entry.output?.example_output ||
        entry.output?.illustrative_example ||
        "";

      if (!example || example === "N/A") {
        return null;
      }

      return prepareIllustrativePdfParts(example);
    }),
  );

  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.height;
  let y = 14;

  y = addPdfText(doc, "Dialectical Analysis Theory (DAT) Output", {
    y,
    pageHeight,
    fontSize: 15,
    fontStyle: "bold",
  });

  y += 2;

  y = addPdfText(doc, `Generated: ${new Date().toLocaleString()}`, {
    y,
    pageHeight,
    fontSize: 9,
  });

  y += 6;

  for (let idx = 0; idx < history.length; idx += 1) {
    const entry = history[idx];
    if (idx > 0) {
      if (y + 20 > pageHeight - 14) {
        doc.addPage();
        y = 14;
      } else {
        y += 6;
      }
    }

    y = addPdfText(doc, `DAT Analysis ${idx + 1}`, {
      y,
      pageHeight,
      fontSize: 13,
      fontStyle: "bold",
    });

    y += 4;

    let sectionNumber = 1;

    const addNumberedSection = (title, body) => {
      y = addPdfSection(doc, `${sectionNumber}. ${title}`, body, { y });

      sectionNumber += 1;
    };

    const addOptionalSubsection = (title, body) => {
      y = addPdfSubsection(doc, title, body, { y });
    };

    const addCredence = (label, value, explanation) => {
      if (value === undefined || value === null) return;

      y = addPdfText(doc, `${label}: ${formatBayes(value)}`, {
        y,
        pageHeight,
        fontSize: 10,
        fontStyle: "normal",
      });

      y += 1;

      y = addPdfText(doc, explanation, {
        y,
        pageHeight,
        fontSize: 8,
        fontStyle: "normal",
      });

      y += 5;
    };

    // 1. User input
    addNumberedSection("User input", entry.input || "(No input found)");

    // Unnumbered input-classification layer
    const classifierRaw = entry.output?.contradiction_classifier || {};

    const classifier =
      classifierRaw &&
      typeof classifierRaw === "object" &&
      !Array.isArray(classifierRaw)
        ? classifierRaw
        : {};

    const domains = Array.isArray(classifierRaw)
      ? classifierRaw
      : Array.isArray(classifier.domain)
        ? classifier.domain
        : typeof classifier.domain === "string"
          ? [classifier.domain]
          : [];

    const resolutionRaw =
      classifier.resolution_display || classifier.resolution || "";

    const classifierLines = [
      domains.length > 0
        ? `Domains: ${domains
            .map((domain) => String(domain).toUpperCase())
            .join(" | ")}`
        : null,

      classifier.subtype ? `Subtype: ${classifier.subtype}` : null,

      classifier.structure ? `Structure: ${classifier.structure}` : null,

      resolutionRaw ? `Resolution: ${prettyResolution(resolutionRaw)}` : null,

      classifier.notes ? `Note: ${classifier.notes}` : null,
    ].filter(Boolean);

    if (classifierLines.length > 0) {
      addOptionalSubsection("Input classification", classifierLines.join("\n"));
    }

    const inquiryOrientation = entry.output?.inquiry_orientation || {};

    const orientationLevels = [
      inquiryOrientation.descriptive
        ? `Descriptive: ${capitalizeFirst(String(inquiryOrientation.descriptive))}`
        : null,
      inquiryOrientation.normative
        ? `Normative: ${capitalizeFirst(String(inquiryOrientation.normative))}`
        : null,
      inquiryOrientation.prescriptive
        ? `Prescriptive: ${capitalizeFirst(String(inquiryOrientation.prescriptive))}`
        : null,
    ].filter(Boolean);

    const orientationText = [
      orientationLevels.join(" | "),
      inquiryOrientation.summary || null,
    ]
      .filter(Boolean)
      .join("\n");

    if (orientationText) {
      addOptionalSubsection("Inquiry orientation", orientationText);
    }

    // 2. Step 1
    addNumberedSection(
      "Step 1 - Contradiction Identification",
      entry.output?.step1_summary || "—",
    );

    addOptionalSubsection(
      "Contradiction Analysis",
      entry.output?.step1_summary_bridge,
    );

    addCredence(
      "Step 1 Prior",
      entry.output?.prior,
      "The 'Prior' probability represents the initial contradiction resolution probability, based on historical persistence and domain evidence.",
    );

    // 3. Step 2
    y = addPdfSection(
      doc,
      `${sectionNumber}. Step 2 - Synthesis Feasibility`,
      [],
      { y },
    );

    sectionNumber += 1;

    addOptionalSubsection("Thesis", entry.output?.thesis);

    addOptionalSubsection("Antithesis", entry.output?.antithesis);

    addOptionalSubsection("Synthesis", entry.output?.synthesis);

    addOptionalSubsection(
      "Synthesis Analysis",
      entry.output?.step2_summary_summary,
    );

    addCredence(
      "Step 2 Likelihood",
      entry.output?.likelihood2,
      "The 'Likelihood' probability represents an evidence-based progression score showing how synthesis addresses the contradiction's structure and feasibility.",
    );

    // 4. Step 3
    addNumberedSection(
      "Step 3 - Contradiction Reconfiguration",
      entry.output?.step3_summary || "—",
    );

    addOptionalSubsection(
      "Contradiction Reconfiguration Analysis",
      entry.output?.step3_summary_summary,
    );

    // 5. Step 4
    addNumberedSection(
      "Step 4 - Theoretical Validation",
      entry.output?.step4_summary || "—",
    );

    const rawMeta =
      entry.output?.step4_dat_metasummary?.meta_summary ||
      (typeof entry.output?.step4_dat_metasummary === "string"
        ? entry.output.step4_dat_metasummary
        : "");

    const safeMeta = sanitizeMetaSummary(rawMeta);

    addOptionalSubsection("DAT Meta-Analysis", safeMeta);

    addCredence(
      "Final Posterior",
      entry.output?.posterior,
      "The 'Final Posterior' indicates the overall contradiction resolution probability — a Bayesian accountability metric integrating priors, synthesis, and evidence.",
    );

    // Reformulated question
    const reformulated =
      entry.output?.final_output ||
      entry.output?.reformulated_alternative ||
      "";

    if (reformulated && reformulated !== "N/A") {
      addNumberedSection("Reformulated Question", reformulated);
    }

    // Illustrative example
    const example =
      entry.output?.example_output || entry.output?.illustrative_example || "";

    if (example && example !== "N/A") {
      const preparedExample = preparedIllustrativeExamples[idx];

      if (preparedExample?.hasMath) {
        y = await addPdfIllustrativeExample(
          doc,
          `${sectionNumber}. Illustrative Example`,
          preparedExample,
          { y },
        );

        sectionNumber += 1;
      } else {
        addNumberedSection(
          "Illustrative Example",
          cleanIllustrativeExample(example),
        );
      }
    }

    // Recommended reading
    const refs = entry.output?.recommended_references;
    let refsArr = [];

    if (Array.isArray(refs)) {
      refsArr = refs;
    } else if (
      refs &&
      typeof refs === "object" &&
      Array.isArray(refs.references)
    ) {
      refsArr = refs.references;
    }

    if (refsArr.length > 0) {
      addNumberedSection(
        "Recommended Reading",
        refsArr.map((ref) =>
          typeof ref === "string" ? ref : JSON.stringify(ref),
        ),
      );
    }
  }

  addPdfFooters(doc, `${DAT_VERSION} | DAT analysis report`);

  const date = new Date().toISOString().split("T")[0];
  doc.save(`dat_history_${date}.pdf`);
}

// Utility: Strips Thesis, Antithesis, Synthesis headers from any text
function cleanTASHeaders(text = "") {
  if (!text) return "";
  let out = String(text);

  // 1) Remove line-leading TAS headers
  out = out.replace(/^\s*(Thesis|Antithesis|Synthesis)\s*[:：].*$/gim, "");

  // 2) Remove inline TAS clauses up to the next hard stop (., ;, line break, or end)
  out = out.replace(/\bThesis\s*[:：]\s*[^.;\n]+[.;]?/gi, "");
  out = out.replace(/\bAntithesis\s*[:：]\s*[^.;\n]+[.;]?/gi, "");
  out = out.replace(/\bSynthesis\s*[:：]\s*[^.;\n]+[.;]?/gi, "");

  // 3) Collapse leftover punctuation/whitespace
  out = out
    .replace(/\s{2,}/g, " ")
    .replace(/(\s*[,:;]){2,}/g, "$1")
    .trim();

  return out;
}

function stripTASHeaders(text = "") {
  // Removes bold or plain Thesis/Antithesis/Synthesis headers and lines.
  return text
    .replace(
      /(\*\*(Thesis|Antithesis|Synthesis)\*\*[:：]?.*?)(?=(\*\*|$|\n))/gis,
      "",
    )
    .replace(/^[\-\*\s]*(Thesis|Antithesis|Synthesis)[:：].*$/gim, "")
    .trim();
}
function capitalizeFirst(s) {
  if (!s || typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Drop this near your other small helpers (e.g., under cleanIllustrativeExample)
function sanitizeMetaSummary(input) {
  if (!input) return "";
  let out = String(input);

  // If there's a fenced JSON block, try to parse it and keep only "summary"
  const fenced = out.match(/```json\s*([\s\S]*?)```/i);
  if (fenced) {
    try {
      const obj = JSON.parse(fenced[1]);
      if (obj && typeof obj.summary === "string" && obj.summary.trim()) {
        // remove the fenced block; if nothing else remains, use the summary
        out = out.replace(fenced[0], "").trim();
        if (!out) out = obj.summary.trim();
      } else {
        out = out.replace(fenced[0], "").trim();
      }
    } catch {
      // not valid JSON—just strip the fenced block
      out = out.replace(fenced[0], "").trim();
    }
  }

  // Remove any remaining fenced code blocks of any language
  out = out.replace(/```[\s\S]*?```/g, "").trim();

  // If the whole thing is just a bare {"summary": "..."} blob, unwrap it
  const loneObj = out.match(/^\s*\{\s*"summary"\s*:\s*"([\s\S]*?)"\s*\}\s*$/i);
  if (loneObj && loneObj[1]) {
    out = loneObj[1].trim();
  }

  // Clean up excessive newlines/whitespace
  out = out.replace(/\n{3,}/g, "\n\n").trim();
  return out;
}

function cleanPara(para = "") {
  return para.replace(/\s*[-–—\.]{3,}\s*$/, "");
}
function cleanIllustrativeExample(text) {
  if (!text || typeof text !== "string") return text || "";

  return (
    text
      // Remove markdown bullets / bold markers.
      .replace(/^\s*[-*•]\s*/gm, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")

      // Repair em/en dashes incorrectly used inside common compounds.
      // Examples handled globally: non-verbal, truth-seeming,
      // human-mediated, meaning-making, knowledge-like,
      // evidence-based, model-driven, AI-generated, etc.
      .replace(
        /\b(non|pre|post|anti|pro|pseudo|quasi|meta|micro|macro|multi|cross|self)\s*[—–]\s*([A-Za-z]+)\b/gi,
        "$1-$2",
      )
      .replace(
        /\b([A-Za-z]+)\s*[—–]\s*([A-Za-z]+)\b/gi,
        (match, left, right) => {
          const compoundForm =
            /(?:like|based|driven|mediated|making|seeming|generated|dependent|specific|sensitive|oriented|related|level)$/i;

          return compoundForm.test(right) ? `${left}-${right}` : match;
        },
      )

      .replace(/\n{2,}/g, "\n")
      .trim()
  );
}
function isEmptyTextContent(t) {
  if (typeof t === "string") return !t.trim();
  if (t === null || t === undefined) return true;
  return false; // Let React nodes pass through
}

// Map raw resolution → hint (used by UI)
const prettyResolution = (raw) => {
  if (!raw) return "";
  const key = String(raw)
    .toLowerCase()
    .replace(/\s*\(.*?\)\s*$/, "");
  const MAP = {
    certain: "Certain (axiomatic)",
    definition: "Definition (semantic)",
    reconciliatory: "Reconciliatory (integrative)",
    irreconcilable: "Irreconcilable (paradox)",
    sublatable: "Sublatable (transformative)",
    empirical: "Empirical (evidence)",
    contextual: "Contextual (relative)",
    unknown: "Unknown (unspecified)",
    unclassified: "Unclassified (unspecified)",
  };
  return MAP[key] ?? String(raw);
};

// Utility: Format Bayes values to 2 decimal places, fallback to "–"
const formatBayes = (value) => {
  if (typeof value !== "number" || isNaN(value)) {
    return "–";
  }

  if (value < 0.01) {
    return "<0.01";
  }

  return value.toFixed(2);
};

// A tiny helper to print classifier details (shows resolution hints)
const renderClassifierDetails = (classifier = {}) => {
  // Show nothing only if ALL fields are missing (incl. resolution_display)
  if (
    !classifier?.subtype &&
    !classifier?.structure &&
    !classifier?.resolution &&
    !classifier?.resolution_display &&
    !classifier?.notes
  ) {
    return null;
  }

  // Local fallback mapper in case backend didn't send resolution_display
  const mapResolution = (raw) => {
    if (!raw) return "";
    const key = String(raw)
      .toLowerCase()
      .replace(/\s*\(.*?\)\s*$/, "");
    const MAP = {
      certain: "Certain (axiomatic)",
      definition: "Definition (semantic)",
      reconciliatory: "Reconciliatory (integrative)",
      irreconcilable: "Irreconcilable (paradox)",
      sublatable: "Sublatable (transformative)",
      empirical: "Empirical (evidence)",
      contextual: "Contextual (relative)",
      unknown: "Unknown (unspecified)",
      unclassified: "Unclassified (unspecified)",
    };
    return MAP[key] ?? String(raw);
  };

  let resolutionText = mapResolution(
    classifier?.resolution_display || classifier?.resolution,
  );

  // Paradox-aware fallback: if backend sent unknown/unclassified/empty, pick a sane default.
  const struct = String(classifier?.structure || "").toLowerCase();
  const subtype = String(classifier?.subtype || "").toLowerCase();
  const rawRes = String(classifier?.resolution || "")
    .toLowerCase()
    .replace(/\s*\(.*?\)\s*$/, "");

  // Paradox takes precedence, regardless of what backend sent
  if (struct.includes("paradox") || subtype.includes("paradox")) {
    resolutionText = "Irreconcilable (paradox)";
  } else {
    // Otherwise, only override when missing/unknown
    const isMissing =
      !resolutionText ||
      rawRes === "" ||
      rawRes === "unknown" ||
      rawRes === "unclassified" ||
      resolutionText.toLowerCase().includes("unknown") ||
      resolutionText.toLowerCase().includes("unclassified");

    if (isMissing) {
      resolutionText = "Contextual (relative)";
    }
  }

  return (
    <div className="ml-1 mt-1 text-xs text-gray-300 space-y-1">
      {classifier?.subtype && (
        <div>
          <span className="text-white font-semibold">Subtype:</span>{" "}
          {capitalizeFirst(String(classifier.subtype))}
        </div>
      )}

      {classifier?.structure && (
        <div>
          <span className="text-white font-semibold">Structure:</span>{" "}
          {capitalizeFirst(String(classifier.structure))}
        </div>
      )}

      {resolutionText && (
        <div>
          <span className="text-white font-semibold">Resolution:</span>{" "}
          {String(resolutionText)}
        </div>
      )}

      {classifier?.notes && (
        <div className="mt-1 text-gray-400 italic">
          {String(classifier.notes)}
        </div>
      )}
    </div>
  );
};

const DAT_VERSION = "DAT v5.0-20260927 | UI v6.1-20261104";
// const VALID_PASSWORD = import.meta.env.VITE_TEST_COHORT_PASSWORD;

// ─────────────────────────────────────────────────────────────────────────────
// Section component (unchanged except logging Step 4 props)
// ─────────────────────────────────────────────────────────────────────────────

// If you need to pass datChallenge, add it as a prop too
function Section({
  title,
  text,
  highlight,
  subtle,
  bayes,
  interpretation,
  causal_model,
  reformulated,
  example_output,
  metaSummary,
  datChallenge, // <-- Add this if you want the challenge block INSIDE Section
  mathJaxConfig,
}) {
  const stepColors = {
    "Step 1 – Contradiction Identification": "text-indigo-500",
    "Step 2 - Synthesis Feasibility": "text-blue-500",
    "Step 3 – Contradiction Reconfiguration": "text-sky-500",
    "Step 4 – Theoretical Validation": "text-cyan-500",
  };

  useEffect(() => {
    if (title === "Step 4 – Theoretical Validation") {
      console.log("🧠 Step 4 Props →", {
        bayes,
        interpretation,
        causal_model,
        reformulated,
        example_output,
        metaSummary,
        datChallenge,
      });
    }
  }, [
    title,
    bayes,
    interpretation,
    causal_model,
    reformulated,
    example_output,
    metaSummary,
    datChallenge,
  ]);
  // At the start of Section, after extracting props:
  if (
    (!text || (typeof text === "string" && !text.trim())) &&
    metaSummary &&
    typeof metaSummary === "object"
  ) {
    return (
      <div>
        <h3
          className={`font-bold mb-2 text-lg ${
            stepColors[title] || "text-indigo-400"
          }`}
        >
          {title}
        </h3>
        <pre className="text-xs text-gray-400 bg-black p-3 rounded">
          {JSON.stringify(metaSummary, null, 2)}
        </pre>
        <p className="italic text-gray-500">
          No parsed summary—showing raw backend output for debugging.
        </p>
      </div>
    );
  }

  // Render fallback if no text
  if (
    (!text || (typeof text === "string" && !text.trim())) &&
    metaSummary &&
    typeof metaSummary === "object"
  ) {
    return (
      <div className="text-gray-600 italic">
        <h3
          className={`font-bold mb-2 text-lg ${
            stepColors[title] || "text-indigo-400"
          }`}
        >
          {title}
        </h3>
        <p>No output available.</p>
      </div>
    );
  }

  // Step 1: Render clean summary in a single paragraph (no MathJax)
  if (
    title === "Step 1 – Contradiction Identification" &&
    typeof text === "string"
  ) {
    // 1) Clean but keep a fallback if cleaning empties the string
    const base = cleanSummary(text, title);
    let cleanedText = base
      .replace(/^\d+\.\s*/gm, "") // strip leading numerals
      .replace(/^\s*[-*•]\s*/gm, "") // strip bullets
      .replace(/\n+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // Fallback if cleaning over-strips
    if (!cleanedText || cleanedText === "N/A")
      cleanedText = String(text).trim();

    // Remove any leftover TAS headers just in case
    cleanedText = cleanTASHeaders(cleanedText);

    // Tidy rare trailing fragment seen in your bridge text
    cleanedText = cleanedText.replace(/\band which\.\s*$/i, ".").trim();

    return (
      <div>
        <h3 className={`font-bold mb-2 text-lg ${stepColors[title]}`}>
          {title}
        </h3>
        <p className="text-gray-200 leading-relaxed">{cleanedText}</p>

        {/* Optional Step 1 meta (plain text; no MathJax) */}
        {(() => {
          const safeMeta = sanitizeMetaSummary(metaSummary);
          if (!safeMeta) return null;
          return (
            <div className="mt-6 mb-8 p-4 rounded bg-gray-900 border-l-4 border-purple-500">
              <h4 className="text-purple-400 font-semibold mb-2">
                Step 1 Meta-Analysis
              </h4>
              <p className="text-gray-200 leading-relaxed text-sm">
                {safeMeta}
              </p>
            </div>
          );
        })()}
      </div>
    );
  }

  // Step 2: If React node, just render it
  if (title === "Step 2 - Synthesis Feasibility" && typeof text !== "string") {
    return (
      <div>
        <h3
          className={`font-bold mb-2 text-lg ${
            highlight ? "text-green-400" : stepColors[title] || "text-blue-500"
          }`}
        >
          {title}
        </h3>
        {text}
      </div>
    );
  }

  // Default: Render paragraphs split on double newlines
  // PATCH: Render Step 3 and Step 4 with MathJax or preformatted JSON
  let cleaned;
  if (typeof text === "string") {
    cleaned = cleanSummary(text, title);
  } else if (React.isValidElement(text)) {
    cleaned = text; // Just use the element, do NOT stringify
  } else if (text && typeof text === "object") {
    cleaned = JSON.stringify(text, null, 2);
  } else {
    cleaned = String(text ?? "");
  }

  let mainContent;

  // Special handling for Step 3 and Step 4
  if (
    (title === "Step 3 – Contradiction Reconfiguration" ||
      title === "Step 4 – Theoretical Validation") &&
    text
  ) {
    if (React.isValidElement(text)) {
      // If text is already a React element (like a MathJaxContext node), just render it!
      mainContent = text;
    } else if (typeof text === "object" && text !== null) {
      mainContent = (
        <pre className="text-xs bg-gray-900 text-white p-3 rounded">
          {JSON.stringify(text, null, 2)}
        </pre>
      );
    } else {
      mainContent = (
        <MathJaxContext config={mathJaxConfig}>
          <MathJax dynamic>{String(text)}</MathJax>
        </MathJaxContext>
      );
    }
  } else if (
    typeof cleaned === "string" &&
    cleaned.trim().length > 0 &&
    cleaned[0] !== "{"
  ) {
    mainContent = cleaned
      .split("\n\n")
      .map((para, idx) => <p key={idx}>{para}</p>);
  } else if (typeof text === "object" && text !== null) {
    mainContent = (
      <pre className="text-gray-400 text-xs bg-gray-900 rounded p-3">
        {cleaned}
      </pre>
    );
  } else {
    mainContent = <p className="italic text-gray-400">No output available.</p>;
  }

  return (
    <div>
      <h3
        className={`font-bold mb-2 text-lg ${
          highlight ? "text-green-400" : stepColors[title] || "text-indigo-400"
        }`}
      >
        {title}
      </h3>
      <div className={`${subtle ? "text-sm text-gray-400" : "space-y-4"}`}>
        {mainContent}
      </div>

      {/* DAT Meta Summary block */}
      {metaSummary && (
        <div className="mt-6 mb-8 p-4 rounded bg-gray-900 border-l-4 border-cyan-500">
          <h3 className="font-bold mb-2 text-lg text-cyan-400">
            DAT Meta-Summary
          </h3>
          <div className="mt-6 text-white text-sm">
            <MathJaxContext config={mathJaxConfig}>
              {metaSummary.split("\n\n").map((para, idx) => (
                <MathJax dynamic key={idx}>
                  {para}
                </MathJax>
              ))}
            </MathJaxContext>
          </div>
        </div>
      )}

      {/* DAT Ideology Challenge */}
      {datChallenge && (
        <div className="mt-4 p-4 rounded bg-yellow-900 border-l-4 border-yellow-400 shadow">
          <strong>DAT Challenge:</strong>
          {datChallenge
            .replace(/^(\s|\n)+/, "")
            .split(/\n{2,}/)
            .map((block, idx) =>
              !block.trim() ? null : (
                <p key={idx} className="mb-2">
                  {block.trim()}
                </p>
              ),
            )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// App component: make sure NO hooks are nested.
// ─────────────────────────────────────────────────────────────────────────────

function App() {
  useEffect(() => {
    appInsights.trackPageView({ name: "DAT Engine Frontend View" });
  }, []);

  useEffect(() => {
    let isMounted = true;
    getAnalyticsInstance()
      .then((analytics) => {
        if (analytics && isMounted) {
          logEvent(analytics, "page_view");
          console.log("✅ Firebase page_view logged");
        } else {
          console.warn("⚠️ Firebase Analytics not available or unsupported");
        }
      })
      .catch((error) => {
        console.error("❌ Firebase Analytics init error:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const backendURL = import.meta.env.VITE_BACKEND_URL;

  // ─── Top‐level hooks: __do not nest these__ ───
  const [stage, setStage] = useState("about");
  const [authenticated, setAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(900);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState(null);
  const [history, setHistory] = useState([]);
  // ─── Expose history for DevTools debugging ───
  useEffect(() => {
    window.datHistory = history;
  }, [history]);
  const [loading, setLoading] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showNot, setShowNot] = useState(false);
  const [showBayes, setShowBayes] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [warning, setWarning] = useState(false);
  const [datChallenge, setDatChallenge] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPage, setShowResetPage] = useState(false);

  // Firebase Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  const handleFirebaseLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      setAuthenticated(true);
    } catch (error) {
      alert("Login failed: " + error.message);
    }
  };

  const mathJaxConfig = {
    loader: {
      load: ["input/tex", "output/chtml", "ui/menu", "a11y/assistive-mml"],
    },
    tex: {
      inlineMath: [
        ["$", "$"],
        ["\\(", "\\)"],
      ],
      displayMath: [
        ["$$", "$$"],
        ["\\[", "\\]"],
      ],
      // Extend defaults with safety packs and a few common macros
      packages: { "[+]": ["base", "ams", "noerrors", "noundefined"] },
      tags: "ams",
      tagSide: "right",
      tagIndent: "0em",
      processEscapes: true,
      processEnvironments: true,
      macros: {
        RR: "\\mathbb{R}",
        NN: "\\mathbb{N}",
        QQ: "\\mathbb{Q}",
        ZZ: "\\mathbb{Z}",
        argmax: "\\operatorname*{arg\\,max}",
        argmin: "\\operatorname*{arg\\,min}",
        E: "\\mathbb{E}",
        Var: "\\operatorname{Var}",
        Cov: "\\operatorname{Cov}",
        Pr: "\\operatorname{Pr}",
        given: "\\,|\\,",
      },
    },
    options: {
      skipHtmlTags: ["script", "noscript", "style", "textarea", "pre", "code"],
    },
    chtml: {
      scale: 1, // 1.0 default font scale
      minScaleAdjust: 100, // prevents tiny formulas
    },
  };

  const timeoutRef = useRef(null);
  const historyEndRef = useRef(null);

  // ─── Idle‐timeout effect (fires when `authenticated` changes) ───
  useEffect(() => {
    if (!authenticated) return;

    const resetTimer = () => setSecondsLeft(900);
    ["mousemove", "keydown", "click"].forEach((e) =>
      window.addEventListener(e, resetTimer),
    );

    timeoutRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timeoutRef.current);
          alert("Session expired. Please log in again.");
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      ["mousemove", "keydown", "click"].forEach((e) =>
        window.removeEventListener(e, resetTimer),
      );
      clearInterval(timeoutRef.current);
    };
  }, [authenticated]);

  // ─── Scroll‐to‐bottom effect (fires whenever `history` changes) ───
  useEffect(() => {
    if (historyEndRef.current) {
      historyEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [history]);

  // ─── Warning effect (fires whenever `history.length` reaches 9) ───
  useEffect(() => {
    if (history.length === 9) {
      setWarning(true);
    } else {
      setWarning(false);
    }
  }, [history]);

  // ─── Utility: format seconds as mm:ss ───
  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  // ───────────────────────────────────────────────────────────────────────────
  //  Helpers (all inside App, but NOT hooks):
  // ───────────────────────────────────────────────────────────────────────────

  // ─── Replacement: extractBayesValues + parseApiResponse ───
  // Helper: read new Bayes fields from backend response JSON
  const extractBayesValues = (res) => ({
    prior: res.data.bayes?.prior ?? null,
    likelihood2:
      res.data.bayes?.likelihood2 ?? res.data.bayes?.likelihood ?? null,
    step2_posterior:
      res.data.bayes?.step2_posterior ?? res.data.bayes?.posterior ?? null,
    likelihood4: res.data.bayes?.likelihood4 ?? 1.0,
    posterior: res.data.bayes?.posterior ?? null,
    bayesian_reasoning: res.data.bayes?.bayesian_reasoning ?? "",
  });

  // Safe field finder: tries top-level, then nested, then parses JSON string fields if needed
  function findField(data, ...paths) {
    for (let path of paths) {
      let val = path.split(".").reduce((o, k) => (o ? o[k] : undefined), data);
      if (typeof val === "string" && val.trim()) {
        // If string looks like JSON, try parsing
        if (val.trim().startsWith("{") && val.includes("summary")) {
          try {
            const parsed = JSON.parse(val);
            if (parsed && parsed.summary) return parsed.summary;
          } catch (e) {
            /* ignore */
          }
        }
        return val;
      }
    }
    return "";
  }

  // Prefer strings; if an object arrives, try common text-y fields.
  function pickBridgeText(d) {
    // direct string candidates first
    const strCandidates = [
      d.step1_summary_bridge,
      d.steps?.step1_summary_bridge,
      d.summary?.step1_summary_bridge,
      d.full_steps?.step1_summary_bridge,
    ];
    for (const v of strCandidates) {
      if (typeof v === "string" && v.trim()) return v.trim();
    }

    // object candidates (backend sometimes uses *_obj)
    const objCandidates = [
      d.step1_summary_bridge_obj,
      d.steps?.step1_summary_bridge_obj,
      d.summary?.step1_summary_bridge_obj,
      d.full_steps?.step1_summary_bridge_obj,
    ];
    for (const obj of objCandidates) {
      if (!obj || typeof obj !== "object") continue;
      const fields = ["text", "summary", "meta_summary", "content", "value"];
      for (const k of fields) {
        const val = obj[k];
        if (typeof val === "string" && val.trim()) return val.trim();
      }
    }

    return "N/A";
  }

  // ────────────────────────────────────────────────────────────────────────────
  // parseApiResponse(res):
  //   Grabs the JSON payload from Axios and remaps it to the exact keys our UI reads.
  // ────────────────────────────────────────────────────────────────────────────
  const parseApiResponse = (res) => {
    const data = res.data || {};

    console.log("PARSE API RAW", data);
    console.log("PARSE DAT_CHALLENGE", data.dat_ideology_challenge);

    // Helper to pick first non-empty, non-"N/A" value
    const pick = (...candidates) =>
      candidates.find(
        (v) => v && v !== "N/A" && typeof v === "string" && v.trim() !== "",
      ) || "N/A";

    // ---- Step 1 ----
    let step1_summary = pick(
      // NEW: the engine’s cleaned one-paragraph Step-1 output
      data.summary?.reflection,
      // existing fallbacks (keep them)
      data.step1_summary,
      data.steps?.step1_summary,
      data.summary?.step1_summary,
      data.full_steps?.step1_summary,
    );

    // Scrub TAS tokens + formatting issues
    if (step1_summary) {
      step1_summary = cleanTASHeaders(step1_summary);

      step1_summary = step1_summary
        .replace(/[-–—]{2,}\s*/g, "") // kill stray “--” or “---”
        .replace(/\b[-–—]?\s*-\s*g\.\s*/gi, "e.g. ") // fix broken “- - g.”
        .replace(/\b\d+\.\s*g\.\s*/gi, "e.g. ") // fix "1. g." or "2. g."
        .replace(/(^|\s)g\.\s*/gi, "$1e.g. ") // fix lonely "g."
        .replace(/\s{2,}/g, " ") // collapse spaces
        .trim();
    }

    // Fallback if backend gave nothing useful
    if (!step1_summary || step1_summary === "N/A" || !step1_summary.trim()) {
      step1_summary = `The input — "${
        data.input || "the question"
      }" — introduces a potential contradiction requiring further analysis.`;
    }

    // Bridge → Contradiction Analysis (scrub separately)
    let step1_summary_bridge = pickBridgeText(data);
    if (step1_summary_bridge && step1_summary_bridge !== "N/A") {
      step1_summary_bridge = cleanTASHeaders(step1_summary_bridge)
        .replace(/[-–—]{2,}\s*/g, "")
        .trim();
    }

    // Meta-summary (unchanged)
    const step1_meta_summary = pick(
      data.step1_summary_meta,
      data.steps?.step1_summary_meta,
      data.summary?.step1_summary_meta,
    );

    // 🔎 Debug log for dev-time sanity check
    console.log("✅ Step1 parsed:", {
      summary: step1_summary,
      bridge: step1_summary_bridge,
      meta: step1_meta_summary,
    });

    // ---- Step 2 ----
    const thesis = pick(
      data.thesis,
      data.step2_summary?.thesis,
      data.steps?.thesis,
      data.full_steps?.thesis,
    );
    const antithesis = pick(
      data.antithesis,
      data.step2_summary?.antithesis,
      data.steps?.antithesis,
      data.full_steps?.antithesis,
    );
    const synthesis = pick(
      data.synthesis,
      data.step2_summary?.synthesis,
      data.steps?.synthesis,
      data.full_steps?.synthesis,
    );
    const step2_summary_summary = pick(
      data.step2_summary_summary,
      data.step2_summary,
      data.steps?.step2_summary,
      data.full_steps?.step2_summary,
    );

    // ---- Step 3 ----
    const step3_summary = pick(
      data.step3_summary,
      data.steps?.step3_summary,
      data.step3_transformation,
      data.summary?.step3_summary,
      data.full_steps?.step3_summary,
    );
    const step3_summary_summary = pick(
      data.step3_summary_summary,
      data.steps?.step3_summary_summary,
      data.summary?.step3_summary_summary,
      data.full_steps?.step3_summary,
    );

    // ---- Step 4 ----
    const step4_summary = pick(
      data.step4_summary,
      data.summary?.step4_summary,
      data.steps?.step4_summary,
      data.full_steps?.step4_summary,
    );
    const step4_dat_metasummary = pickMetaSummary(
      data.step4_dat_metasummary,
      data.summary?.step4_dat_metasummary,
    );

    // ---- Illustrative Example ----
    const example_output = pick(
      data.example_output,
      data.steps?.example_output,
      data.summary?.example_output,
    );

    // ---- Reformulation & Example ----
    const alt = pick(
      data.reformulated_alternative,
      data.final_output,
      data.reformulated,
      data.steps?.reformulated,
      data.summary?.reformulated,
    );
    const related_question = generateRelatedQuestion(alt);

    // ---- Output object ----
    return {
      step1_summary,
      step1_summary_bridge,
      step1_meta_summary,
      thesis,
      antithesis,
      synthesis,
      step2_summary_summary,
      step3_summary,
      step3_summary_summary,
      step4_summary,
      step4_dat_metasummary,
      reformulated_alternative: alt,
      related_question,

      // FLATTENED Bayes for PDF, plus grouped object for UI
      prior: data.bayes?.prior ?? 0,
      likelihood2: data.bayes?.likelihood2 ?? data.bayes?.likelihood ?? 0,
      step2_posterior:
        data.bayes?.step2_posterior ?? data.bayes?.posterior ?? 0,
      likelihood4: data.bayes?.likelihood4 ?? 1.0,
      posterior: data.bayes?.posterior ?? 0,
      bayesian_reasoning: data.bayes?.bayesian_reasoning ?? "",
      bayes: {
        prior: data.bayes?.prior ?? 0,
        likelihood: data.bayes?.likelihood ?? data.bayes?.likelihood2 ?? 0,
        likelihood2: data.bayes?.likelihood2 ?? data.bayes?.likelihood ?? 0,
        step2_posterior:
          data.bayes?.step2_posterior ?? data.bayes?.posterior ?? 0,
        likelihood4: data.bayes?.likelihood4 ?? 1.0,
        posterior: data.bayes?.posterior ?? 0,
        bayesian_reasoning: data.bayes?.bayesian_reasoning ?? "",
      },

      // Robust illustrative example (try all possible keys)
      illustrative_example:
        pick(
          data.illustrative_example,
          data.example_output,
          data.steps?.illustrative_example,
          data.steps?.example_output,
          data.summary?.illustrative_example,
          data.summary?.example_output,
        ) || "",

      // Redundant for older fields
      example_output:
        pick(
          data.example_output,
          data.illustrative_example,
          data.steps?.example_output,
          data.summary?.example_output,
        ) || "",

      // References (old and new)
      references:
        data.references ||
        data.recommended_references?.references ||
        data.recommended_references ||
        [],

      // Recommended references (always normalized to array)
      recommended_references: Array.isArray(data.recommended_references)
        ? data.recommended_references
        : data.recommended_references?.references || data.references || [],

      contradiction_classifier: data.classifier || data.epistemic_domain || {},
      inquiry_orientation:
        data.inquiry_orientation ||
        data.pipeline_result?.inquiry_orientation ||
        {},
      causal_model: data.causal_model || {},
      final_output: pick(
        data.final_output,
        data.summary?.final_output,
        data.steps?.final_output,
        data.reformulated_alternative,
        data.reformulated_question,
      ),
      dat_ideology_challenge: data.dat_ideology_challenge || "",

      // Raw API dump (good for debugging)
      raw: data,
    };
  };

  const pickMetaSummary = (...candidates) => {
    for (const v of candidates) {
      if (!v) continue; // Skip null/undefined
      // CASE 1: v is an object with a valid .meta_summary string
      if (
        typeof v === "object" &&
        v.meta_summary &&
        typeof v.meta_summary === "string" &&
        v.meta_summary.trim() !== ""
      ) {
        return v; // <-- return the whole object as-is!
      }
      // CASE 2: v is a string (rare, old backend output)
      if (typeof v === "string" && v !== "N/A" && v.trim() !== "") {
        return { meta_summary: v }; // wrap as object for consistency
      }
    }
    // If nothing found, always return a consistent empty object
    return { meta_summary: "" };
  };

  // ────────────────────────────────────────────────────────────────────────────
  //  Add this helper so the UI actually saves each response into `history`
  // ────────────────────────────────────────────────────────────────────────────
  const updateStateFromResponse = (
    setResponse,
    setHistory,
    setQuery,
    newResponse,
    originalQuery,
  ) => {
    console.log("↪ updateStateFromResponse called with:", {
      newResponse,
      originalQuery,
    });

    // 1) Keep the latest parsed response (in case you use `response` elsewhere)
    setResponse(newResponse);

    // 2) Push a new { input, output } pair into history
    setHistory((prevHistory) => {
      const lastEntry = prevHistory[prevHistory.length - 1];
      if (
        lastEntry &&
        lastEntry.input === originalQuery &&
        JSON.stringify(lastEntry.output) === JSON.stringify(newResponse)
      ) {
        // Don't add duplicate
        return prevHistory;
      }
      // Enforce max 10 entries
      const next = [
        ...prevHistory,
        { input: originalQuery, output: newResponse },
      ];
      return next.length > 10 ? next.slice(-10) : next;
    });

    // 3) Clear the textarea so the user can type again
    setQuery("");
  };

  // ───────────────────────────────────────────────────────────────────────────
  //  handleSubmit (inside App, not nested)
  // ───────────────────────────────────────────────────────────────────────────

  const handleSubmit = async (eOrQuery) => {
    if (eOrQuery && eOrQuery.preventDefault) eOrQuery.preventDefault();
    const finalQuery = typeof eOrQuery === "string" ? eOrQuery : query;
    if (!finalQuery || loading) return;

    setLoading(true);
    setResponse(null);

    try {
      const raw = await analyze(finalQuery);

      // Extract and parse
      const newResponse = parseApiResponse({ data: raw });

      setDatChallenge(newResponse.dat_ideology_challenge || "");
      updateStateFromResponse(
        setResponse,
        setHistory,
        setQuery,
        newResponse,
        finalQuery,
      );

      // Optional analytics (safe fields only)
      try {
        const analytics = await getAnalyticsInstance();
        if (analytics) {
          logEvent(analytics, "dat_analysis", {
            input_question: finalQuery,
            dat_answer:
              newResponse.step4_summary ||
              newResponse.step3_summary ||
              newResponse.step2_summary_summary ||
              newResponse.step1_summary ||
              "",
            reformulated_question:
              newResponse.reformulated_alternative ||
              newResponse.reformulated_question ||
              "",
          });
        }
      } catch (_) {
        /* ignore analytics errors */
      }
    } catch (error) {
      console.error("handleSubmit error:", error);
      setResponse({
        error:
          error?.message || "Unknown error occurred. See console for details.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  //  Render login screen if not yet authenticated
  // ───────────────────────────────────────────────────────────────────────────
  if (!authenticated) {
    // Show the ResetPasswordPage if "forgot password" is triggered
    if (showResetPage) {
      return (
        <div className="min-h-screen bg-gray-950 text-white py-12 px-4 flex flex-col items-center justify-center">
          <div className="fixed top-2 right-4 text-xs text-gray-500">
            {DAT_VERSION}
          </div>
          <Logo className="w-48 h-auto mb-6" />
          <ResetPasswordPage onBack={() => setShowResetPage(false)} />
        </div>
      );
    }

    // Default login screen
    return (
      <div className="min-h-screen bg-gray-950 text-white py-12 px-4 flex flex-col items-center justify-center">
        <div className="fixed top-2 right-4 text-xs text-gray-500">
          {DAT_VERSION}
        </div>
        <h1 className="text-3xl font-bold mb-2">Dialectical Analysis Theory</h1>
        <p className="text-sm text-gray-400 mb-6">
          Intellectual honesty | Epistemic clarity
        </p>
        <Logo className="w-48 h-auto mb-6" />
        <div className="bg-gray-900 p-6 rounded-xl shadow-md w-full max-w-sm text-left">
          <form onSubmit={handleFirebaseLogin}>
            <input
              type="email"
              className="form-element w-full mb-2"
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              placeholder="Email"
              autoComplete="username"
              required
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="form-element w-full mb-2 pr-14"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 text-xs text-gray-500"
                style={{ transform: "translateY(-50%)" }}
                tabIndex={-1}
                onClick={() => setShowPassword((show) => !show)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <button
              type="submit"
              className="w-full mt-4 bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded"
            >
              Sign In
            </button>
          </form>
          <button
            onClick={() => setShowResetPage(true)}
            className="mt-3 underline text-blue-400 hover:text-blue-300 text-xs"
            type="button"
          >
            Forgot password?
          </button>
        </div>
        {/* Toggle About DAT */}
        <div className="mt-6">
          <button
            onClick={() => setShowAbout((prev) => !prev)}
            className="underline text-sm text-blue-400 hover:text-blue-300"
          >
            {showAbout ? "Hide About DAT" : "About DAT"}
          </button>
        </div>

        {showAbout && (
          <div className="mt-10 w-full">
            <DatPublicAbout />
          </div>
        )}
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  //  Render “About” screen if we haven’t started yet
  // ───────────────────────────────────────────────────────────────────────────
  if (stage === "about") {
    return (
      <div className="text-center bg-gray-950 text-white min-h-screen flex flex-col justify-center items-center px-4">
        <div className="fixed top-2 right-4 text-xs text-gray-500">
          {DAT_VERSION}
        </div>
        <h1 className="text-3xl font-bold mb-2">Dialectical Analysis Theory</h1>
        <p className="text-sm text-gray-400 mb-6">
          Intellectual honesty | Epistemic clarity
        </p>
        <Logo className="w-48 h-auto mb-6" />

        {/* Three info dropdowns */}
        <div className="flex flex-row justify-center space-x-8 mb-6">
          {/* Left: What is DAT? */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowAbout((prev) => !prev)}
              className="text-green-400 hover:text-green-300 underline text-sm"
            >
              What is DAT?
            </button>
            {showAbout && (
              <div className="max-w-xs text-left text-gray-300 text-sm space-y-4 px-4 mt-2 z-10 bg-gray-800 rounded-xl shadow-lg">
                <About />
              </div>
            )}
          </div>

          {/* Center: What DAT is Not */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowNot((prev) => !prev)}
              className="text-green-400 hover:text-green-300 underline text-sm"
            >
              What DAT is Not
            </button>
            {showNot && (
              <div className="max-w-xs text-left text-gray-300 text-sm space-y-4 px-4 mt-2 z-10 bg-gray-800 rounded-xl shadow-lg">
                <WhatDATisNot />
              </div>
            )}
          </div>

          {/* Right: DAT & Bayes */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowBayes((prev) => !prev)}
              className="text-green-400 hover:text-green-300 underline text-sm"
            >
              DAT &amp; Bayes
            </button>
            {showBayes && (
              <div className="max-w-xs text-left text-gray-300 text-sm space-y-4 px-4 mt-2 z-10 bg-gray-800 rounded-xl shadow-lg">
                <DATandBayes />
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowPrivacy((prev) => !prev)}
          className="mt-4 text-gray-400 hover:text-gray-300 underline text-sm"
        >
          Privacy & Disclaimer
        </button>

        {showPrivacy && (
          <div className="mt-4 max-w-2xl text-left text-blue-300 text-sm space-y-4 px-4">
            <PrivacyDisclaimer />
          </div>
        )}
        <button
          onClick={() => setStage("input")}
          className="mt-8 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-xl font-semibold"
        >
          Begin Analysis
        </button>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  //  Finally: main “analysis” screen
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 text-white pb-40 px-4 flex flex-col items-center">
      {/* DAT version in top‐right */}
      <div className="fixed top-2 right-4 text-xs text-gray-500 z-10">
        {DAT_VERSION}
      </div>

      {/* ─── WARNING BANNER (history.length === 9) ─── */}
      {warning && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-black px-4 py-2 rounded shadow-md z-10">
          ⚠️ You have generated 9 outputs. The next (10th) result will clear
          older outputs – save now if required.
        </div>
      )}

      {/* ─── Logo + Heading ─── */}
      <div className="flex items-center justify-center gap-4 mb-6 mt-4">
        <Logo className="h-14 hidden sm:block" />
        <h1 className="text-3xl font-extrabold text-indigo-400 tracking-tight leading-tight">
          Dialectical Analysis Theory
          <sup className="text-xs align-super">™</sup>
        </h1>
        <Logo className="h-14 hidden sm:block" />
      </div>

      {/* ─── History entries ─── */}
      {history.length > 0 && (
        <div className="w-full max-w-2xl mb-40 space-y-12 text-sm animate-fade-in">
          {history.map((entry, idx) => (
            <div
              key={idx}
              className="space-y-6 border-b-4 border-indigo-800 pb-8"
            >
              {/* Question header */}
              <h2 className="text-xl font-semibold text-white border-b border-gray-700 pb-2">
                DAT Input {idx + 1}: {entry.input}
              </h2>
              {/* Domain bubble — single source of truth */}
              {renderDomainBubble(entry.output?.contradiction_classifier || {})}

              {/* Orthogonal inquiry-orientation axis */}
              {entry.output?.inquiry_orientation &&
                (entry.output.inquiry_orientation.descriptive ||
                  entry.output.inquiry_orientation.normative ||
                  entry.output.inquiry_orientation.prescriptive ||
                  entry.output.inquiry_orientation.summary) && (
                  <div className="mt-3 ml-2 text-sm text-gray-300 space-y-1">
                    <div>
                      <span className="font-semibold text-white">
                        Inquiry orientation:
                      </span>{" "}
                      <span>
                        Descriptive:{" "}
                        {capitalizeFirst(
                          String(
                            entry.output.inquiry_orientation.descriptive ||
                              "absent",
                          ),
                        )}
                        {" | "}
                        Normative:{" "}
                        {capitalizeFirst(
                          String(
                            entry.output.inquiry_orientation.normative ||
                              "absent",
                          ),
                        )}
                        {" | "}
                        Prescriptive:{" "}
                        {capitalizeFirst(
                          String(
                            entry.output.inquiry_orientation.prescriptive ||
                              "absent",
                          ),
                        )}
                      </span>
                    </div>

                    {entry.output.inquiry_orientation.summary && (
                      <div className="text-gray-400 italic">
                        {String(entry.output.inquiry_orientation.summary)}
                      </div>
                    )}

                    <details className="mt-2 text-xs text-gray-400">
                      <summary className="cursor-pointer select-none text-indigo-300 hover:text-indigo-200">
                        What do these mean?
                      </summary>

                      <div className="mt-2 ml-1 pl-3 border-l border-gray-700 space-y-1">
                        <div>
                          <span className="font-semibold text-gray-300">
                            Descriptive
                          </span>
                          {" — "}
                          what is the case, what is being explained or
                          identified.
                        </div>

                        <div>
                          <span className="font-semibold text-gray-300">
                            Normative
                          </span>
                          {" — "}
                          what standards, criteria or warrants are used to
                          assess it.
                        </div>

                        <div>
                          <span className="font-semibold text-gray-300">
                            Prescriptive
                          </span>
                          {" — "}
                          what should be done, chosen or changed.
                        </div>

                        <div className="mt-3 pt-2 border-t border-gray-700">
                          <span className="font-semibold text-gray-300">
                            Levels
                          </span>
                          <div className="mt-1 space-y-1">
                            <div>
                              <span className="font-semibold text-gray-300">
                                Absent
                              </span>
                              {" — "}
                              not present in the inquiry.
                            </div>
                            <div>
                              <span className="font-semibold text-gray-300">
                                Secondary
                              </span>
                              {" — "}
                              present, but peripheral to the inquiry.
                            </div>
                            <div>
                              <span className="font-semibold text-gray-300">
                                Substantial
                              </span>
                              {" — "}
                              important to answering the inquiry.
                            </div>
                            <div>
                              <span className="font-semibold text-gray-300">
                                Dominant
                              </span>
                              {" — "}
                              the primary functional demand of the inquiry.
                            </div>
                          </div>
                        </div>
                      </div>
                    </details>
                  </div>
                )}

              {/* JSON fields from Step 1.0 */}
              {entry.output.step1_0_output && (
                <div className="mt-4 ml-2 text-sm text-gray-300 space-y-2">
                  <div>
                    <span className="font-semibold text-white">Terms:</span>{" "}
                    {entry.output.step1_0_output.terms?.join(", ") || "N/A"}
                  </div>
                  <div>
                    <span className="font-semibold text-white">
                      Contradiction Flags:
                    </span>{" "}
                    {entry.output.step1_0_output.contradiction_flags?.join(
                      ", ",
                    ) || "None"}
                  </div>
                  <div>
                    <span className="font-semibold text-white">
                      Symbolic Irregularities:
                    </span>{" "}
                    {entry.output.step1_0_output.symbolic_irregularities?.join(
                      ", ",
                    ) || "None"}
                  </div>
                  <div>
                    <span className="font-semibold text-white">
                      Epistemic Instability:
                    </span>{" "}
                    {entry.output.step1_0_output.epistemic_instability ||
                      "None"}
                  </div>
                  <div>
                    <span className="font-semibold text-white">
                      Ontological Tags:
                    </span>{" "}
                    {entry.output.step1_0_output.ontological_tags?.join(", ") ||
                      "None"}
                  </div>
                  <div>
                    <span className="font-semibold text-white">
                      Step References:
                    </span>{" "}
                    {entry.output.step1_0_output.references?.join("; ") ||
                      "None"}
                  </div>
                </div>
              )}

              {/* Step 1 — render plain summary, no refs */}
              <Section
                title="Step 1 – Contradiction Identification"
                text={cleanTASHeaders(
                  cleanSummary(entry.output.step1_summary, "Step 1"),
                )}
                bayes={entry.output.bayes}
                interpretation={entry.output.interpretation}
                metaSummary={
                  entry.output.step1_summary_meta?.meta_reflection ||
                  entry.output.summary?.meta_reflection ||
                  entry.output.step1_summary_meta ||
                  entry.output.meta_reflection ||
                  ""
                }
                mathJaxConfig={mathJaxConfig}
              />

              {history.length - 1 === idx &&
                (() => {
                  const step1_summary_bridge =
                    entry.output?.step1_summary_bridge || "N/A";
                  if (
                    /thesis[:：]?|antithesis[:：]?|synthesis[:：]?/i.test(
                      step1_summary_bridge,
                    )
                  ) {
                    console.warn(
                      "⚠️ Bridge summary may still include TAS elements!",
                    );
                  }
                  return null;
                })()}
              {/* Step 1 Bridge Summary */}
              {entry.output.step1_summary_bridge &&
                entry.output.step1_summary_bridge !== "N/A" && (
                  <div className="mt-6 mb-8 p-4 rounded bg-gray-800 border-l-4 border-indigo-500">
                    <h4 className="text-indigo-500 font-semibold mb-2">
                      Contradiction Analysis
                    </h4>
                    <p className="text-gray-200 leading-relaxed text-sm">
                      {cleanTASHeaders(
                        cleanSummary(
                          entry.output.step1_summary_bridge,
                          "Step 1",
                        ),
                      )}
                    </p>
                  </div>
                )}

              {/* ─── Step 1 – Prior ─── */}
              <span className="relative inline-block group">
                <span className="text-xs italic text-green-400 underline decoration-dotted cursor-help">
                  Step 1 Prior: {formatBayes(entry.output.bayes.prior)}
                </span>
                <div
                  className="
                                    pointer-events-none
                                    absolute
                                    bottom-full
                                    mb-2
                                    left-0
                                    sm:left-1/2
                                    sm:-translate-x-1/2
                                    opacity-0
                                    transition-opacity
                                    duration-150
                                    group-hover:opacity-100
                                    z-50
                                  "
                >
                  <div
                    className="
                                      bg-indigo-700
                                      text-white
                                      italic
                                      text-sm
                                      leading-relaxed
                                      text-left
                                      px-4
                                      py-2
                                      rounded-2xl
                                      shadow-xl
                                      max-w-md
                                      whitespace-normal
                                    "
                  >
                    The 'Prior' probability represents the initial contradiction
                    resolution probability, based on historical persistence and
                    domain evidence.
                  </div>
                </div>
              </span>
              <Step2Tension output={entry.output} />
              {entry.output.step2_summary_summary &&
                entry.output.step2_summary_summary !== "N/A" && (
                  <div className="mt-6 mb-8 p-4 rounded bg-gray-800 border-l-4 border-blue-500">
                    <h4 className="text-blue-500 font-semibold mb-2">
                      Synthesis Analysis
                    </h4>
                    <p className="text-gray-200 leading-relaxed text-sm">
                      {cleanTASHeaders(
                        cleanSummary(
                          entry.output.step2_summary_summary,
                          "Step 2",
                        ),
                      )}
                    </p>
                  </div>
                )}
              {/* ─── Step 2 – Likelihood ─── */}
              <span className="relative inline-block group ml-4">
                <span className="text-xs italic text-green-400 underline decoration-dotted cursor-help">
                  Step 2 Likelihood:{" "}
                  {formatBayes(entry.output.bayes.likelihood)}
                </span>
                <div
                  className="
                    pointer-events-none
                    absolute
                    bottom-full
                    mb-2
                    left-0
                    sm:left-1/2
                    sm:-translate-x-1/2
                    opacity-0
                    transition-opacity
                    duration-150
                    group-hover:opacity-100
                    z-50
                  "
                >
                  <div
                    className="
                      bg-indigo-700
                      text-white
                      italic
                      text-sm
                      leading-relaxed
                      text-left
                      px-4
                      py-2
                      rounded-2xl
                      shadow-xl
                      max-w-md
                      whitespace-normal
                    "
                  >
                    The ‘Likelihood’ probability represents an evidence-based
                    progression score showing how synthesis addresses the
                    contradiction’s structure and feasibility
                  </div>
                </div>
              </span>
              {/* ───────────── Step 3 – Contradiction Reconfiguration ───────────── */}
              {entry.output.step3_summary ? (
                <Section
                  title="Step 3 – Contradiction Reconfiguration"
                  text={entry.output.step3_summary}
                  mathJaxConfig={mathJaxConfig}
                />
              ) : (
                <div className="text-gray-500 italic mt-2">
                  No Step 3 output available.
                </div>
              )}
              {/* Step 3 – Profound Meta-Summary (DAT Bridge) */}
              {entry.output.step3_summary && (
                <MathJaxContext config={mathJaxConfig}>
                  <div className="mt-6 mb-8 p-4 rounded bg-gray-800 border-l-4 border-sky-500">
                    <h4 className="text-sky-300 font-semibold mb-2">
                      Contradiction Reconfiguration Analysis
                    </h4>
                    <MathJax dynamic>
                      {injectCitationsIntoText(
                        entry.output.step3_summary_summary,
                        entry.output.references,
                      )}
                    </MathJax>
                  </div>
                </MathJaxContext>
              )}
              {/* ───────────── Step 4 – Theoretical Validation ───────────── */}
              {entry.output.step4_summary ? (
                <Section
                  title="Step 4 – Theoretical Validation"
                  text={
                    <MathJaxContext config={mathJaxConfig}>
                      <MathJax dynamic>
                        {injectCitationsIntoText(
                          entry.output.step4_summary,
                          getRelevantCitations(entry.output.step4_summary, 2),
                        )}
                      </MathJax>
                    </MathJaxContext>
                  }
                  bayes={entry.output.bayes || {}}
                  interpretation={entry.output.interpretation || ""}
                  causal_model={
                    entry.output.causal_model ||
                    entry.output.step4_structured_output?.causal_model ||
                    entry.output.steps?.step4_structured_output?.causal_model ||
                    {}
                  }
                  reformulated={
                    entry.output.reformulated_alternative ||
                    entry.output.step4_structured_output?.reformulated ||
                    entry.output.steps?.step4_structured_output?.reformulated ||
                    entry.output.reformulated_question ||
                    ""
                  }
                  example_output={
                    entry.output.example_output ||
                    entry.output.step4_structured_output?.example_output ||
                    entry.output.steps?.step4_structured_output
                      ?.example_output ||
                    ""
                  }
                  mathJaxConfig={mathJaxConfig} // <-- Add this at the end!
                />
              ) : (
                <div className="text-gray-500 italic mt-2">
                  No Step 4 output available.
                </div>
              )}
              {/* ───────────── Step 4 Explanation (Dynamic) ───────────── */}
              {entry.output.bayes_explanation &&
                entry.output.bayes_explanation.text && ( // only render if text is nonempty
                  <div className="mt-4 p-4 bg-gray-800 rounded-lg text-sm text-gray-200">
                    <h4 className="font-semibold text-white mb-2">
                      Why Posterior ={" "}
                      {formatBayes(entry.output.bayes.posterior)}?
                    </h4>
                    <p className="text-white mb-2">
                      {entry.output.bayes_explanation.text}
                    </p>
                    {entry.output.bayes_explanation.hints?.length > 0 && (
                      <div className="mt-2">
                        <h5 className="font-semibold text-gray-300 mb-1">
                          Tips to Deepen Understanding:
                        </h5>
                        <ul className="list-disc pl-5 text-gray-300">
                          {entry.output.bayes_explanation.hints.map(
                            (hint, idx) => (
                              <li key={idx}>{hint}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              {/* Step 4 Meta-Summary */}
              {(() => {
                const rawMeta =
                  entry.output.step4_dat_metasummary?.meta_summary || "";
                const safeMeta = sanitizeMetaSummary(rawMeta);
                if (!safeMeta) return null;
                return (
                  <div className="mt-6 mb-8 p-4 rounded bg-gray-900 border-l-4 border-cyan-500">
                    <h4 className="text-cyan-300 font-semibold mb-2">
                      DAT Meta-Analysis
                    </h4>
                    <MathJaxContext config={mathJaxConfig}>
                      <MathJax dynamic>{safeMeta}</MathJax>
                    </MathJaxContext>
                  </div>
                );
              })()}

              {/* ─── Step 4 – Final Posterior (wider, auto‐formatted bubble) ─── */}
              <span className="relative inline-block group ml-4">
                <span className="text-xs italic text-green-400 underline decoration-dotted cursor-help">
                  Final Posterior: {formatBayes(entry.output.bayes.posterior)}
                </span>
                <div
                  className="
                                    pointer-events-none
                                    absolute
                                    bottom-full
                                    mb-2
                                    left-0
                                    sm:left-1/2
                                    sm:-translate-x-1/2
                                    opacity-0
                                    transition-opacity
                                    duration-150
                                    group-hover:opacity-100
                                    z-50
                                  "
                >
                  <div
                    className="
                                      bg-indigo-700
                                      text-white
                                      italic
                                      text-sm          /* slightly larger than text-[0.75rem] */
                                      leading-relaxed  /* a bit more line-height for readability */
                                      text-left
                                      px-4             /* wider horizontal padding */
                                      py-2             /* slightly taller vertical padding */
                                      rounded-2xl      /* extra‐soft rounded corners */
                                      shadow-xl
                                      max-w-md         /* ≈28rem wide (≈448px) */
                                      whitespace-normal
                                    "
                  >
                    The ‘Final Posterior’ indicates the overall contradiction
                    resolution probability — a Bayesian accountability metric
                    integrating priors, synthesis, and evidence
                  </div>
                </div>
              </span>

              {/* Reformulated Question (card) */}
              {entry.output.final_output && (
                <>
                  <h3 className="font-bold mb-2 text-lg text-green-400">
                    Reformulated Question
                  </h3>
                  <div className="bg-gray-900 text-white p-6 rounded-lg border-2 border-gray-600 text-base shadow-sm font-bold text-lg mb-4 prose prose-invert max-w-none">
                    <ReactMarkdown
                      children={entry.output.final_output}
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        // This makes sure paragraph tags don’t add weird spacing
                        p: ({ children }) => <span>{children}</span>,
                      }}
                    />
                  </div>
                </>
              )}
              {/* Illustrative Example */}
              {(entry.output.example_output ||
                entry.output.illustrative_example) && (
                <section className="pt-4">
                  <h3 className="font-bold mb-2 text-lg text-indigo-400 text-left">
                    Illustrative Example
                  </h3>

                  {/* Card container handles horizontal overflow for wide equations */}
                  <div className="illustrative-card overflow-x-auto rounded-2xl border border-slate-700/70 bg-slate-900/60 p-4">
                    {/* Keep prose styling, but let lines wrap normally */}
                    <div className="prose prose-invert max-w-none whitespace-normal">
                      <IllustrativeExample
                        example={cleanIllustrativeExample(
                          entry.output.example_output ||
                            entry.output.illustrative_example,
                        )}
                      />
                    </div>
                  </div>
                </section>
              )}

              {/* 📚 Recommended Reading */}
              {(() => {
                const refs = Array.isArray(entry.output.recommended_references)
                  ? entry.output.recommended_references
                  : entry.output.recommended_references?.references || [];

                if (!refs || refs.length === 0) return null;

                const APA_ROW_RE =
                  /^(?<authors>.+?)\s*\((?<year>\d{4}|n\.d\.)\)\.\s*(?<title>.+?)\.?\s*$/i;

                return (
                  <div className="mt-6 mb-8 p-4 rounded bg-gray-800 border-l-4 border-purple-500">
                    <h4 className="text-purple-300 font-semibold mb-3">
                      DAT Recommended Reading
                    </h4>

                    <div className="text-gray-300 text-sm space-y-0.5 leading-snug">
                      {refs.map((raw, i) => {
                        const s =
                          typeof raw === "string" ? raw : JSON.stringify(raw);
                        const m = s.match(APA_ROW_RE);
                        if (!m || !m.groups) return <div key={i}>{s}</div>;

                        const { authors, year } = m.groups;
                        let title = m.groups.title.trim();
                        if (!title.endsWith(".")) title += ".";

                        return (
                          <div
                            key={i}
                            className="
                              grid items-center gap-x-3 py-0.5
                              [grid-template-columns:max-content_1fr]
                              sm:[grid-template-columns:max-content_1fr]
                            "
                          >
                            <span className="font-semibold whitespace-nowrap">
                              {authors}{" "}
                              <span className="font-normal text-neutral-400 tabular-nums">
                                ({year}).
                              </span>
                            </span>

                            <span className="italic text-neutral-200">
                              {title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* “Try Asking” bubble */}
              {(() => {
                const question = entry.output.related_question;
                if (!question || question === "N/A") return null;

                return (
                  <div className="mt-4 text-xs text-gray-400">
                    Try Asking <span className="font-medium">➙</span> 
                    <button
                      type="button"
                      className="text-xs text-gray-200 hover:underline"
                      onClick={() => {
                        setQuery(question);
                        const ta = document.querySelector("textarea");
                        if (ta) ta.focus();
                      }}
                    >
                      {question}
                    </button>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* scroll target */}
      <div ref={historyEndRef} />

      {/* ─── Input Area (fixed to bottom) ─── */}
      <form
        onSubmit={handleSubmit}
        className="fixed bottom-0 left-0 w-full bg-gray-950 px-4 pt-4 pb-2 flex flex-col items-center z-10"
      >
        <div className="w-full max-w-2xl flex gap-2">
          {/* Logout Button */}
          <button
            type="button"
            onClick={async () => {
              try {
                await auth.signOut();
                setAuthenticated(false);
                setAuthEmail("");
                setAuthPassword("");
                window.location.reload(); // optional: refresh state/UI
              } catch (err) {
                alert("Logout failed: " + err.message);
              }
            }}
            className="bg-gray-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition"
            style={{ minWidth: "80px" }} // To match button height/width
          >
            Log Out
          </button>
          <textarea
            className="form-element w-full min-h-[3.5rem] resize-none rounded-lg"
            placeholder="Ask any question..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-semibold text-white whitespace-nowrap ${
              loading ? "bg-gray-600" : "bg-indigo-600 hover:bg-indigo-500"
            }`}
          >
            {loading ? "Processing..." : "Submit"}
          </button>
        </div>
        <div className="w-full max-w-2xl text-center mt-2 text-[0.65rem] text-gray-500">
          DAT Engine is methodical, not fast | Processing may take up to 60
          seconds | A 'save' feature activates priror to data deletion after 9
          entries | DAT Engine uses a static GPT model (trained to November
          2024) | gpt-4o can make mistakes.
        </div>
      </form>

      {/* Session Timer */}
      <div className="fixed top-2 left-4 bg-gray-800 text-gray-300 px-3 py-1 rounded-md text-xs shadow z-10">
        ⏳
        <span className="text-white font-semibold">
          {formatTime(secondsLeft)}
        </span>{" "}
        left
      </div>

      {/* Save PDF Button */}
      <button
        onClick={async () => {
          try {
            await downloadHistoryAsPDF(history);
          } catch (error) {
            console.error("PDF generation error:", error);
            alert(`PDF generation failed:\n${error?.message || String(error)}`);
          }
        }}
        className="fixed bottom-6 left-6 z-10 bg-gray-800 text-white px-4 py-2 rounded-full shadow-lg text-xs hover:bg-gray-700 transition"
        title="Save current analysis history as PDF"
      >
        Save PDF
      </button>

      {/* Clear Button */}
      <button
        onClick={() => {
          setHistory([]);
          setResponse(null);
          setQuery("");
        }}
        className="fixed bottom-6 left-28 z-10 bg-gray-800 text-white px-4 py-2 rounded-full shadow-lg text-xs hover:bg-red-700 transition"
        title="Clear current analysis history"
      >
        Clear
      </button>

      {/* Feedback Button */}
      <a
        href="mailto:dat@dialectix.com.au"
        className="fixed bottom-6 right-6 z-10 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg text-xs hover:bg-green-500 transition"
        title="Send feedback to DAT"
      >
        Feedback
      </a>
    </div>
  );
}
// Router wrapper (keeps your app + adds reset routes)
function AppWithRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Request a reset link (user types email) */}
        <Route
          path="/reset"
          element={<ResetPasswordPage onBack={() => window.history.back()} />}
        />

        {/* Confirm reset (clicked from email) */}
        <Route path="/auth/reset" element={<ResetPasswordConfirm />} />

        {/* Everything else -> your main app */}
        <Route path="*" element={<App />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppWithRouter;
export { Section };
